import { useState, useRef, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { classNames } from '~/utils/classNames';
import type { AskUserQuestion } from '~/types/context';

interface AskUserQuestionsProps {
  questions: AskUserQuestion[];
  onSubmit: (answers: { questionId: string; answer: string }[]) => void;
  isSubmitted: boolean;
}

export const AskUserQuestions = memo(({ questions, onSubmit, isSubmitted }: AskUserQuestionsProps) => {
  const [currentStep, setCurrentStep] = useState(isSubmitted ? questions.length : 0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [customText, setCustomText] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(isSubmitted);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const advance = useCallback(
    (questionId: string, answer: string, step: number) => {
      const newAnswers = { ...answers, [questionId]: answer };
      setAnswers(newAnswers);

      const nextStep = step + 1;

      if (nextStep >= questions.length) {
        setCurrentStep(nextStep);
        setSubmitted(true);

        const payload = questions.map((q) => ({
          questionId: q.id,
          answer: newAnswers[q.id] ?? '',
        }));
        onSubmit(payload);
      } else {
        setCurrentStep(nextStep);
      }
    },
    [answers, questions, onSubmit],
  );

  const handleOptionClick = useCallback(
    (questionId: string, label: string, step: number) => {
      if (submitted) {
        return;
      }

      advance(questionId, label, step);
    },
    [submitted, advance],
  );

  const handleCustomSubmit = useCallback(
    (questionId: string, step: number) => {
      const text = (customText[questionId] || '').trim();

      if (!text || submitted) {
        return;
      }

      advance(questionId, text, step);
    },
    [customText, submitted, advance],
  );

  const handleSkip = useCallback(() => {
    if (submitted) {
      return;
    }

    setSubmitted(true);
    setCurrentStep(questions.length);

    const payload = questions.map((q) => ({
      questionId: q.id,
      answer: answers[q.id] ?? '',
    }));
    onSubmit(payload);
  }, [submitted, questions, answers, onSubmit]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="rounded-lg border border-hairline bg-surface px-4 py-3.5 mb-3"
    >
      <AnimatePresence mode="sync">
        {questions.map((q, qIndex) => {
          const isAnswered = qIndex < currentStep;
          const isActive = qIndex === currentStep && !submitted;
          const isPending = qIndex > currentStep && !submitted;

          return (
            <motion.div
              key={q.id}
              layout
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.15, delay: qIndex * 0.03 }}
              className="py-2 first:pt-0"
            >
              {/* Answered — collapsed summary */}
              {isAnswered && (
                <motion.div layout className="flex items-start gap-2">
                  <span className="i-ph:check text-success text-[12px] flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-muted">{q.question}</div>
                    <div className="text-[12.5px] text-primary mt-0.5">{answers[q.id]}</div>
                  </div>
                </motion.div>
              )}

              {/* Active — show options as chips */}
              {isActive && (
                <motion.div layout>
                  <div className="text-[13px] text-primary mb-2">{q.question}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {q.options.map((option, oIndex) => (
                      <button
                        key={oIndex}
                        type="button"
                        onClick={() => handleOptionClick(q.id, option.label, qIndex)}
                        className={classNames(
                          // Options can be sentence-length — let them wrap instead of overflowing the card.
                          'ui-chip !whitespace-normal text-left',
                          answers[q.id] === option.label && '!border-accent !text-accent !bg-accent/[0.08]',
                        )}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {/* Custom input */}
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      ref={(el) => {
                        inputRefs.current[q.id] = el;
                      }}
                      type="text"
                      placeholder="Or type your own…"
                      value={customText[q.id] || ''}
                      onChange={(e) => setCustomText((prev) => ({ ...prev, [q.id]: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCustomSubmit(q.id, qIndex);
                        }
                      }}
                      className="ui-input !py-1.5 !text-[12px]"
                    />
                  </div>
                </motion.div>
              )}

              {/* Pending — dimmed */}
              {isPending && (
                <motion.div layout className="flex items-start gap-2 opacity-40">
                  <span className="ui-dot ui-dot-draft mt-1.5" />
                  <span className="text-[13px] text-secondary">{q.question}</span>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>

      {!submitted &&
        (() => {
          const activeQuestion = questions[currentStep];
          const activeText = activeQuestion ? (customText[activeQuestion.id] || '').trim() : '';
          const canSend = Boolean(activeQuestion) && activeText.length > 0;

          return (
            <div className="flex items-center gap-2 mt-3.5">
              <button
                type="button"
                disabled={!canSend}
                onClick={() => {
                  if (!activeQuestion || !canSend) {
                    return;
                  }

                  handleCustomSubmit(activeQuestion.id, currentStep);
                }}
                className="ui-btn-accent !px-3.5 !py-1.5 !text-[12px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {/* Mid-questions this sends ONE typed answer and advances; only
                    on the last question does it submit the whole set — label
                    accordingly so it doesn't read as "submit everything" early. */}
                {currentStep >= questions.length - 1 ? 'Send answers' : 'Send'}
              </button>
              <button type="button" onClick={handleSkip} className="ui-btn-ghost">
                Skip
              </button>
            </div>
          );
        })()}
    </motion.div>
  );
});
